/**
 * SessionProgressRing - Apple Watch-style progress ring
 *
 * Fills as the user accepts rewrites during an inline annotation session.
 * Uses "endowed progress" (starts at 20%) to boost completion rates
 * (research shows ~34% more completion with endowed progress).
 *
 * Only renders when 3+ issues exist to avoid nearly-full rings.
 */

import { useEffect } from 'react';
import { View, StyleSheet } from 'react-native';
import Svg, { Circle } from 'react-native-svg';
import Animated, {
  useSharedValue,
  useAnimatedProps,
  withTiming,
  withSequence,
  withSpring,
  useAnimatedStyle,
} from 'react-native-reanimated';

const AnimatedCircle = Animated.createAnimatedComponent(Circle);

const MIN_ISSUES_THRESHOLD = 3;
const ENDOWED_PROGRESS = 0.2;
const ANIMATION_DURATION_MS = 300;

interface SessionProgressRingProps {
  /** Number of accepted rewrites */
  accepted: number;
  /** Total number of issues */
  total: number;
  /** Ring diameter in pixels */
  size?: number;
  /** Ring stroke width in pixels */
  strokeWidth?: number;
  /** Test ID for e2e testing */
  testID?: string;
}

export function SessionProgressRing({
  accepted,
  total,
  size = 40,
  strokeWidth = 3,
  testID = 'session-progress-ring',
}: SessionProgressRingProps) {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;

  // Endowed progress: start at 20% + actual progress fills remaining 80%
  const actualProgress = total > 0 ? accepted / total : 0;
  const displayProgress = ENDOWED_PROGRESS + actualProgress * (1 - ENDOWED_PROGRESS);
  const clampedProgress = Math.min(1, displayProgress);
  const isComplete = accepted === total && total > 0;

  const progress = useSharedValue(clampedProgress);
  const scale = useSharedValue(1);

  useEffect(() => {
    progress.value = withTiming(clampedProgress, { duration: ANIMATION_DURATION_MS });

    // Completion pulse animation
    if (isComplete) {
      scale.value = withSequence(
        withSpring(1.15, { damping: 6, stiffness: 200 }),
        withSpring(1, { damping: 8, stiffness: 150 }),
      );
    }
  }, [clampedProgress, isComplete]); // eslint-disable-line react-hooks/exhaustive-deps

  const animatedProps = useAnimatedProps(() => ({
    strokeDashoffset: circumference * (1 - progress.value),
  }));

  const animatedContainerStyle = useAnimatedStyle(() => ({
    transform: [{ scale: scale.value }],
  }));

  // Don't render for fewer than MIN_ISSUES_THRESHOLD issues
  // (guard placed after ALL hooks to satisfy Rules of Hooks)
  if (total < MIN_ISSUES_THRESHOLD) return null;

  return (
    <Animated.View
      style={[ringStyles.container, animatedContainerStyle]}
      testID={testID}
      accessibilityRole="progressbar"
      accessibilityValue={{
        min: 0,
        max: total,
        now: accepted,
      }}
    >
      <Svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {/* Background track */}
        <Circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="rgba(255,255,255,0.2)"
          strokeWidth={strokeWidth}
          fill="none"
        />
        {/* Progress fill */}
        <AnimatedCircle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="rgba(255,255,255,0.9)"
          strokeWidth={strokeWidth}
          fill="none"
          strokeDasharray={`${circumference}`}
          animatedProps={animatedProps}
          strokeLinecap="round"
          rotation={-90}
          origin={`${size / 2}, ${size / 2}`}
        />
      </Svg>
    </Animated.View>
  );
}

const ringStyles = StyleSheet.create({
  container: {
    alignItems: 'center',
    justifyContent: 'center',
  },
});
